import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ProfileDropdown from "./ProfileDropdown";
import { Link } from "react-router-dom";
import { NAVIGATION } from "../lib/navigation";
import logo from "../assets/images/logo_new.png";
import { FaBell, FaCommentDots } from "react-icons/fa";
import {
  addUnreadMessage,
  fetchUnreadMessages,
  markMessageAsRead,
  selectChatLoading,
  selectHasFetchedNotifications,
  selectUnreadMessages,
} from "../store/slices/api/inner/chatSlice";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { socket } from "../socket";
import { selectLoggedInData } from "../store/slices/api/auth/loginSlice";

const Header = ({ toggleSidebar }) => {
  const dispatch = useDispatch();
  const loggedInData = useSelector(selectLoggedInData);
  const loadingNotifications = useSelector(selectChatLoading);

  const notifications = useSelector(selectUnreadMessages);
  const [showDropdown, setShowDropdown] = useState(false);
  const hasFetchedNotifications = useSelector(selectHasFetchedNotifications);

  useEffect(() => {
    if (loggedInData?.authUserData?.admin_id) {
      const admin_id = loggedInData?.authUserData?.admin_id;
      socket.emit("joinRoom", { user_id: admin_id, is_admin: true });
    }
    socket.on("messageRead", ({ chat_id }) => {
      dispatch(markMessageAsRead(chat_id));
    });

    socket.on("receiveMessage", (newMessage) => {
      if (newMessage?.message_by === "user") {
        dispatch(addUnreadMessage(newMessage));
      }
    });

    return () => {
      socket.off("messageRead");
      socket.off("receiveMessage");
    };
  }, [dispatch, loggedInData]);

  const navigate = useNavigate();
  useEffect(() => {
    if (!hasFetchedNotifications) {
      dispatch(fetchUnreadMessages());
    }
  }, [dispatch, hasFetchedNotifications]);

  // Redirect to chats page to chat with this user_id User
  const openChatWithUser = ({ user_id, chat_id, isAnonymous = false }) => {
    socket.emit("markAsRead", { chat_id });

    dispatch(markMessageAsRead(chat_id));

    navigate(`/chats?user_id=${user_id}&isAnonymous=${isAnonymous}`);
  };
  return (
    <div className="flex flex-wrap fixed top-0 left-0 w-full z-10 bg-gray-50 items-center justify-between mx-auto sm:px-6">
      <div className="flex justify-start gap-10 relative px-4 mt-3">
        <Link
          to={NAVIGATION.INNER_NAVIGATION.DASHBOARD}
          className="flex items-center space-x-3 rtl:space-x-reverse"
        >
          <img src={logo} className="h-14" alt="Roof Reveal Logo" />
        </Link>
      </div>

      <div className="flex items-center gap-4 relative">
        {/* Notification Icon */}
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="relative text-gray-600 hover:text-black"
          >
            {/* First */}
            <FaBell size={24} />

            <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center">
              {loadingNotifications ? (
                <AiOutlineLoading3Quarters
                  size={14}
                  className="animate-spin text-gray-500"
                />
              ) : (
                notifications.length > 0 && (
                  <span className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {notifications.length}
                  </span>
                )
              )}
            </span>
          </button>

          {/* Notification Dropdown */}
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-80 bg-[#fbe8b0] shadow-lg rounded-lg overflow-hidden">
              {/* Header */}
              <div className="p-3 border-b bg-[#f5d67d] text-gray-900 font-semibold flex justify-between">
                <span>Unread Messages</span>
                <button
                  onClick={() => setShowDropdown(false)}
                  className="text-gray-700 text-lg"
                >
                  ×
                </button>
              </div>

              {/* Messages List */}
              <div className="max-h-72 overflow-y-auto">
                {notifications.length > 0 ? (
                  notifications.map((notif, index) => (
                    <div
                      key={index}
                      className="flex items-center p-3 hover:bg-[#f5d67d] cursor-pointer border-b"
                      onClick={() => {
                        openChatWithUser({
                          user_id: notif?.is_anonymous
                            ? notif.anonymous_user_id
                            : notif.user_id,
                          chat_id: notif.chat_id,
                          isAnonymous: notif.is_anonymous,
                        });
                      }}
                    >
                      <FaCommentDots className="text-gray-700 mr-3" size={20} />
                      <div className="flex-1">
                        <p className="font-bold text-gray-900">
                          {notif.is_anonymous
                            ? `${notif.user?.name}`
                            : `${notif.user?.name} ${notif.user?.surname}`}
                        </p>
                        <div className="bg-white text-gray-800 p-2 rounded-lg shadow-md border border-gray-300">
                          {notif.message}
                        </div>
                        <p className="text-xs text-gray-700 mt-1">
                          {new Date(notif.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="p-3 text-sm text-gray-900 text-center">
                    No unread messages
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Profile Dropdown */}
        <ProfileDropdown />

        {/* Mobile Menu Button */}
        <button
          onClick={toggleSidebar}
          type="button"
          className="inline-flex items-center p-2 mt-2 ms-3 text-sm text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200"
        >
          <span className="sr-only">Open sidebar</span>
          <svg
            className="w-6 h-6"
            aria-hidden="true"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              clipRule="evenodd"
              fillRule="evenodd"
              d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
            ></path>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Header;
